import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  completeProfile,
  fetchCurrentUser
} from "../../features/auth/authSlice";
import {
  selectAuthLoading,
  selectCurrentUser
} from "../../features/auth/authSelectors";
import { ReactComponent as CameraIcon } from "../../assets/camera.svg";
import { ReactComponent as EditIcon } from "../../assets/edit.svg";
import { ChevronLeft, Delete } from "@mui/icons-material";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Avatar,
  Box,
  styled,
  Alert,
  useMediaQuery,
  IconButton
} from "@mui/material";
import useSignedUrl from "../../utils/useSignedUrl";
import { getUniqueSuffix } from "../../utils/getUniqueSuffix";
import { uploadFile } from "../../utils/uploadFile";
import { deleteFile } from "../../utils/deleteFile";
import useThemeColor from "../../utils/useTheme";
import dayjs from "dayjs";
import useIsMobile from "../Extra/useMobile";
import CountryDropdown from "../Extra/CountryPicker";

const StyledTextFieldContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  margin: theme.spacing(4, 0)
}));

const StyledInputLabel = styled("label")(({ styledColors }) => ({
  position: "absolute",
  top: "-18px",
  color: styledColors.inputLabelColor,
  fontWeight: "bold",
  fontSize: "14px",
  pointerEvents: "none"
}));

const StyledTextField = styled(TextField)(({ styledColors }) => ({
  input: {
    color: styledColors.color,
    ":disabled": {
      backgroundColor: "#A7C8E9",
      color: styledColors.color,
      borderRadius: "4px"
    }
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      color: "#9CA3AF",
      borderColor: styledColors.lightGreyColor
    },
    "&:hover fieldset": {
      borderColor: styledColors.color
    },
    "&.Mui-focused fieldset": {
      color: "#9CA3AF",
      borderColor: styledColors.color
    },
    "&.Mui-disabled": {
      "& input": {
        color: "red",
        cursor: "not-allowed"
      },
      "& fieldset": {
        borderColor: styledColors.lightGreyColor
      }
    }
  }
}));

const EditProfile = ({ setOpenSubscriptionModal }) => {
  const userData = useSelector(selectCurrentUser);
  const styles = useThemeColor();
  const [formData, setFormData] = useState({
    fullName: userData.fullName || "",
    displayName: userData.displayName || "",
    country: userData.country || "US",
    profilePic: null,
    email: userData.email || "",
    bio: userData.bio || "",
    url: userData.url || ""
  });
  const [formErrors, setFormErrors] = useState({
    fullName: false,
    displayName: false,
    bio: false,
    url: false
  });
  const isInMobile = useIsMobile();
  const matches = useMediaQuery("(max-width:590px)") || isInMobile;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const dispatch = useDispatch();
  const loading = useSelector(selectAuthLoading);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox"
          ? checked
          : name === "bio"
          ? value.length < 51
            ? value
            : formData?.bio
          : value
    });

    if (name !== "bio") {
      setFormErrors({ ...formErrors, [name]: value.trim() === "" });
    }
  };
  const handleCountryChange = (countryCode) => {
    handleInputChange({
      target: {
        name: "country",
        value: countryCode
      }
    });
  };
  const handleEditClick = () => {
    // Programmatically click the hidden file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (
      !userData?.isFailedPayment &&
      !(
        userData?.isAdmin === 1 ||
        ["Coach", "Company"].includes(userData?.userType)
      ) &&
      !userData?.isSubscriptionActive &&
      (userData?.isTrainingFreeTrialExpired ||
        userData?.isDiaryFreeTrialExpired)
    ) {
      setOpenSubscriptionModal(true);
    }
  }, [userData]);

  useEffect(() => {
    if (formData.isJoiningThroughSource) {
      setFormData((prevState) => ({
        ...prevState,
        referenceId: ""
      }));
    }
  }, [formData.userType, formData.isJoiningThroughSource]);
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 30 * 1024 * 1024) {
      setSnackbarMessage("File size should be less than 30MB.");
      setOpenSnackbar(true);
      return;
    }
    if (file) {
      setFormData((prevState) => ({
        ...prevState,
        profilePic: file
      }));
    }
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    if (Object.values(formErrors).some((error) => error)) {
      setSnackbarMessage("Please correct the form errors.");
      setOpenSnackbar(true);
      return;
    }
    try {
      const { profilePic, ...restFormData } = formData;
      let finalFormData = restFormData;

      // Handle profile picture update
      if (profilePic) {
        const uniqueFileName = getUniqueSuffix(profilePic.name);
        finalFormData.profilePic = uniqueFileName;
      } else if (img === "") {
        // If img is empty string, it means user deleted the profile picture
        finalFormData.profilePic = null;
      }
      // If neither condition is met, profilePic field won't be included in finalFormData

      delete finalFormData.email;

      const data = await dispatch(
        completeProfile({
          formData: finalFormData,
          userId: userData?.userId,
          date: dayjs().format("YYYY-MM-DD")
        })
      );

      // Handle file upload/deletion only if successful
      if (data) {
        if (profilePic) {
          // Upload new profile picture
          await uploadFile(
            profilePic,
            finalFormData.profilePic,
            process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
          );
        }

        // Delete old profile picture if it exists and user either uploaded new one or removed it
        if (userData?.profilePic && (profilePic || img === "")) {
          await deleteFile(
            userData.profilePic,
            process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
          );
        }

        setSnackbarMessage("Profile updated successfully.");
        setOpenSnackbar(true);
        dispatch(fetchCurrentUser(dayjs().format("YYYY-MM-DD")));
        navigate("/profile");
      } else {
        setSnackbarMessage("Profile update failed.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("=====", error);
      setSnackbarMessage("Profile update failed.");
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const imgSrc = useSignedUrl(
    userData?.profilePic,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );

  const [img, setImg] = useState("");
  useEffect(() => {
    setImg(imgSrc);
  }, [userData?.profilePic, imgSrc]);

  return !(
    userData?.isAdmin === 1 || ["Coach", "Company"].includes(userData?.userType)
  ) &&
    !userData?.isSubscriptionActive &&
    (userData?.isTrainingFreeTrialExpired ||
      userData?.isDiaryFreeTrialExpired) ? (
    <></>
  ) : (
    <Box
      color={styles.color}
      backgroundColor={styles.background}
      display={"flex"}
      width="100%"
      overflow="auto"
      flexDirection="column"
    >
      <Box display="flex" alignItems="center" gap={3} p="32px 0 0 20px">
        <IconButton onClick={() => navigate("/profile")}>
          <ChevronLeft
            sx={{ height: "35px", width: "35px", fill: styles.color }}
            height={35}
            width={35}
          />
        </IconButton>
        <Typography
          sx={{
            ...(matches && { display: "flex", justifyContent: "center" })
          }}
          fontSize="24px"
        >
          Edit Profile
        </Typography>

        <Snackbar
          open={openSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarMessage.includes("success") ? "success" : "error"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
      <Box
        flexGrow={1}
        paddingY={3}
        paddingLeft={10}
        sx={{
          width: { xs: "100%", md: "60%" },
          paddingTop: { xs: 2, md: 4 },
          paddingRight: { xs: 2, md: 13, lg: 20 },
          paddingBottom: { xs: 8, md: 2 },
          paddingLeft: { xs: 2, md: 10 },
          overflowY: { md: "auto" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start"
        }}
      >
        <Box
          component={"form"}
          sx={{
            ...(matches && { marginBottom: { xs: 9 } })
          }}
          onSubmit={handleUpdate}
        >
          <input
            accept="image/*"
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="profile-pic-upload"
          />
          <Box
            sx={{
              ...(matches && { justifyContent: "center" })
            }}
            display="flex"
            mb={6}
            gap="50px"
            alignItems="center"
          >
            {formData.profilePic ? (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <label htmlFor="profile-pic-upload">
                  <Avatar
                    alt="Profile Pic"
                    src={URL.createObjectURL(formData.profilePic)}
                    sx={{
                      width: 124,
                      height: 124,
                      marginTop: 2
                    }}
                  />
                  <IconButton
                    onClick={handleEditClick}
                    sx={{
                      position: "absolute",
                      bottom: 10, // Adjust this value to move the icon slightly outside the avatar if desired
                      right: 5, // Adjust this value to move the icon slightly outside the avatar if desired
                      backgroundColor: "#368AD0",
                      borderRadius: "50%",
                      height: 20,
                      width: 20,
                      fontSize: 24, // Adjust the size as needed
                      padding: "2px",
                      svg: {
                        path: {
                          fill: "#fff"
                        }
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      setFormData({ ...formData, profilePic: null })
                    }
                    sx={{
                      position: "absolute",
                      bottom: 0, // Adjust this value to move the icon slightly outside the avatar if desired
                      right: 28, // Adjust this value to move the icon slightly outside the avatar if desired
                      backgroundColor: "#ff4d4f",
                      borderRadius: "50%",
                      height: 18,
                      width: 18,
                      svg: {
                        path: {
                          fill: "#fff"
                        }
                      }
                    }}
                  >
                    <Delete style={{ height: "15px", width: "15px" }} />
                  </IconButton>
                </label>
              </Box>
            ) : img ? (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <label htmlFor="profile-pic-upload">
                  <Avatar
                    style={{
                      borderRadius: "48%",
                      backgroundColor: "#ccc",
                      width: 124,
                      height: 124,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: 16,
                      cursor: "pointer"
                    }}
                    src={img}
                  />
                  {/* Edit icon positioned at the bottom-right corner of the avatar */}
                  <IconButton
                    onClick={handleEditClick}
                    sx={{
                      position: "absolute",
                      bottom: 10, // Adjust this value to move the icon slightly outside the avatar if desired
                      right: 5, // Adjust this value to move the icon slightly outside the avatar if desired
                      backgroundColor: "#368AD0",
                      borderRadius: "50%",
                      height: 20,
                      width: 20,
                      fontSize: 24, // Adjust the size as needed
                      padding: "2px",
                      svg: {
                        path: {
                          fill: "#fff"
                        }
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setFormData({ ...formData, profilePic: null });
                      setImg("");
                    }}
                    sx={{
                      position: "absolute",
                      bottom: 0, // Adjust this value to move the icon slightly outside the avatar if desired
                      right: 28, // Adjust this value to move the icon slightly outside the avatar if desired
                      backgroundColor: "#ff4d4f",
                      borderRadius: "50%",
                      height: 18,
                      width: 18,
                      svg: {
                        path: {
                          fill: "#fff"
                        }
                      }
                    }}
                  >
                    <Delete style={{ height: "15px", width: "15px" }} />
                  </IconButton>
                </label>
              </Box>
            ) : (
              <label htmlFor="profile-pic-upload">
                <Button
                  variant="contained"
                  component="span"
                  color="default"
                  style={{
                    borderRadius: "48%",
                    backgroundColor: "#ccc",
                    width: 130,
                    height: 130,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 16,
                    cursor: "pointer"
                  }}
                >
                  <CameraIcon style={{ width: 24, height: 24 }} />
                </Button>
              </label>
            )}
          </Box>
          <StyledTextFieldContainer>
            <StyledInputLabel styledColors={styles} htmlFor="fullName">
              Name
            </StyledInputLabel>
            <StyledTextField
              styledColors={styles}
              fullWidth
              variant="outlined"
              name="fullName"
              placeholder="Enter your Full name"
              value={formData.fullName}
              onChange={handleInputChange}
              margin="normal"
            />
          </StyledTextFieldContainer>
          <StyledTextFieldContainer>
            <StyledInputLabel styledColors={styles} htmlFor="displayName">
              Username (Displayed in app)
            </StyledInputLabel>
            <StyledTextField
              styledColors={styles}
              fullWidth
              variant="outlined"
              name="displayName"
              sx={{ marginTop: "20px" }}
              placeholder="Enter your Username"
              value={formData.displayName}
              onChange={handleInputChange}
              margin="normal"
            />
          </StyledTextFieldContainer>
          <StyledTextFieldContainer>
            <StyledInputLabel styledColors={styles} htmlFor="bio">
              Short Bio (50 character max)
            </StyledInputLabel>
            <StyledTextField
              styledColors={styles}
              sx={{
                "& .MuiInputBase-root": {
                  color: styles.color
                }
              }}
              fullWidth
              variant="outlined"
              name="bio"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              placeholder="Enter your bio (optional)"
              value={formData.bio}
              multiline
              rows={4}
              onChange={handleInputChange}
              margin="normal"
              error={formErrors.bio}
              helperText={
                formErrors.bio && "Bio should be less than 50 characters"
              }
            />
            <Typography
              variant="caption"
              sx={{
                color: formData.bio.length > 50 ? "red" : "#D1D5DB",
                position: "absolute",
                fontSize: "15px",
                right: 10,
                bottom: 10
              }}
            >
              {formData.bio.length}/50
            </Typography>
          </StyledTextFieldContainer>

          <StyledTextFieldContainer>
            <StyledInputLabel styledColors={styles} htmlFor="email">
              Email Address
            </StyledInputLabel>
            <StyledTextField
              styledColors={styles}
              fullWidth
              variant="outlined"
              name="email"
              type="email"
              disabled
              placeholder="Enter your email address"
              value={formData.email}
              onChange={() => {}}
              margin="normal"
            />
          </StyledTextFieldContainer>

          <StyledTextFieldContainer>
            <StyledInputLabel styledColors={styles} htmlFor="url">
              Profile link (website, social media, etc.)
            </StyledInputLabel>
            <StyledTextField
              styledColors={styles}
              fullWidth
              sx={{ marginTop: "20px" }}
              variant="outlined"
              name="url"
              placeholder="Enter URL (optional)"
              value={formData.url}
              onChange={handleInputChange}
              margin="normal"
            />
          </StyledTextFieldContainer>
          {/* {userData?.country && ( */}
            <CountryDropdown
              value={formData.country} // This should be the country code
              onChange={handleCountryChange}
            />
          {/* )} */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{
              marginTop: 3.1,
              backgroundColor: "#368AD0",
              cursor: "pointer",
              color: "#fff",
              width: "100%",
              height: "42px",
              fontWeight: "bold",
              textTransform: "none",
              ":disabled": {
                backgroundColor: "#A7C8E9",
                color: "#64AEDD"
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EditProfile;
